<template>
  <div v-clickoutside="closeUserMenu" class="user-menu" :class="lightTheme && 'light-theme'">
    <div class="dropdown-label" @click="handleUserMenuLabelClick">
      <slot name="dropdown-label">
        <span class="user-name">{{ userName }}</span>
        <i class="el-icon-caret-bottom dropdown-icon"></i>
        <span v-if="notificationCount" class="notification-count">{{ notificationCount | formatNotificationCount }}</span>
      </slot>
    </div>
    <div v-show="lightTheme ? true : isShowUserMenu" class="dropdown-list" :class="dropdownListClass" @transitionend="onTransitionEnd">
      <div v-if="showDropDownArrow" class="dropdown-arrow"></div>
      <template v-for="(item, index) in userMenuList">
        <div
          v-if="item.show"
          class="dropdown-item custom-color"
          :key="index"
          @click="handleUserMenuItemClick(item)">
          <span>{{ item.label }}</span>
          <span v-if="item.isNotification && notificationCount" class="notification-count">{{ notificationCount | formatNotificationCount }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ROOT_COMPANY_ID } from '@/constant/company'
import Clickoutside from 'element-ui/src/utils/clickoutside'
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  directives: {
    Clickoutside
  },
  props: {
    lightTheme: {
      type: Boolean,
      default: false
    },
    isPreview: {
      type: Boolean,
      default: false
    },
    showDropDownArrow: {
      type: Boolean,
      default: false
    },
    isMSDCompany: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      userName: '',
      isShowUserMenu: false,
      notificationCount: 0,
      dropdownListClass: null
    }
  },
  computed: {
    ...mapState('account', ['currentAccount']),
    ...mapGetters('account', [
      'isCandidateAccount',
      'isCompanyAccount',
      'isHeadhunterAccount',
      'hasEmployeeAccountInfo'
    ]),
    MSDCompanyId () {
      if (this.$route.meta?.customCompanyId) {
        return this.$route.meta?.customCompanyId
      }
      return ROOT_COMPANY_ID.MSD
    },
    companyId () {
      const companyId = this.$route.query.company_id || this.$route.params.companyId
      if (!companyId && this.isMSDCompany) {
        return this.MSDCompanyId
      }
      return companyId
    },
    userMenuList () {
      const crmPermission = {
        strict: false,
        isMatchAnd: true,
        any: [
          ['PERM_MICROSITE_CAMPAIGN_MANAGEMANT', 'PREMIUM_MICROSITE_CAMPAIGN_MANAGEMENT'],
          ['PERM_CANDIDATE_SURVEY_MANAGEMENT', 'PREMIUM_PREFERENCE_SURVEY_MANAGEMENT'],
          ['PERM_TAG_MANAGEMANT', 'PREMIUM_TAG_MANAGEMENT']
        ]
      }
      const companyId = this.companyId
      const menuList = [
        {
          label: this.$t('admin.layout.header.userMenu.searchJob'),
          path: `${companyId ? `/recruiting/company/${companyId}/job-list` : '/jobseekers'}`,
          show: this.isCandidateAccount && !this.hasEmployeeAccountInfo
        },
        {
          label: this.$t('admin.layout.header.userMenu.home'),
          path: '/',
          show: this.isCompanyAccount
        },
        {
          label: this.$t('admin.layout.header.userMenu.myHomePage'),
          isInclude: true,
          path: `/recruiting/personal/home${companyId ? `?company_id=${companyId}` : ''}`,
          show: this.isCandidateAccount && !this.hasEmployeeAccountInfo
        },
        {
          label: this.$t('admin.layout.header.userMenu.myHomePage'),
          isInclude: true,
          path: `/recruiting/personal/referral${companyId ? `?company_id=${companyId}` : ''}`,
          show: this.isCandidateAccount && this.hasEmployeeAccountInfo
        },
        {
          label: this.$t('admin.layout.header.userMenu.recruitingManagement'),
          isInclude: true,
          path: '/v-agency/job-list/',
          show: this.isHeadhunterAccount
        },
        {
          label: this.$t('admin.layout.header.userMenu.companyJobList'),
          isInclude: true,
          path: '/v-admin/job-list/',
          show: this.isCompanyAccount && this.hasAnyPermission('PERM_JOB_GET')
        },
        {
          label: this.$t('admin.layout.header.userMenu.crmManagement'),
          isInclude: true,
          path: '/v-admin/crm-management/',
          show: this.isCompanyAccount && this.hasAnyPermission(crmPermission)
        },
        {
          label: this.$t('admin.layout.header.userMenu.editCompanyProfile'),
          isInclude: true,
          path: '/v-admin/edit-company-profile',
          show: this.isCompanyAccount && this.hasAnyPermission(['PERM_COMPANY_PROFILE_EDIT'])
        },
        {
          label: this.$t('admin.layout.header.userMenu.editCompanyCulture'),
          isInclude: true,
          path: '/v-admin/company-culture',
          show: this.isCompanyAccount && this.hasAnyPermission('PERM_COMPANY_CULTURE_EDIT') && !this.hasAnyPermission('COMPUTED_COMPANY_SURVEY_TYPE_B')
        },
        {
          label: this.$t('admin.layout.header.userMenu.changePassword'),
          isInclude: true,
          path: this.handleChangePasswordRoute(),
          show: true
        },
        {
          label: this.$t('admin.layout.header.userMenu.systemNotification'),
          isInclude: true,
          path: '/v-admin/notification/list/',
          show: this.isCompanyAccount,
          isNotification: true
        },
        {
          label: this.$t('admin.layout.header.userMenu.logout'),
          path: '/logout/',
          show: true
        }
      ]

      return menuList
    }
  },
  created () {
    if (this.isCompanyAccount) {
      this.userName = this.currentAccount.full_name
      this.getNotificationCount().then(resp => {
        if (resp) {
          this.notificationCount = resp.unread_count
        }
      })
    } else if (this.isCandidateAccount) {
      this.userName = this.currentAccount.full_name
    } else if (this.isHeadhunterAccount || this.hasEmployeeAccountInfo) {
      this.userName = this.currentAccount.first_name
    }
  },
  methods: {
    ...mapActions('notification', ['getNotificationCount']),
    closeUserMenu () {
      this.isShowUserMenu = false
    },
    handleUserMenuLabelClick () {
      if (this.isPreview) {
        return
      }
      this.isShowUserMenu = !this.isShowUserMenu
    },
    handleUserMenuItemClick (menuItem) {
      this.isShowUserMenu = false
      if (menuItem.isInclude) {
        this.$router.push(menuItem.path)
      } else {
        window.location.href = menuItem.path
      }
    },
    onTransitionEnd (event) {
      if (this.isShowUserMenu) {
        this.dropdownListClass = 'show'
      } else {
        this.dropdownListClass = null
      }
    },
    handleChangePasswordRoute () {
      const companyId = this.$route.query.company_id || this.$route.params.companyId
      if (this.isCandidateAccount) {
        return `/recruiting/personal/functions/change-password/${companyId ? `?company_id=${companyId}` : ''}`
      }
      if (this.isHeadhunterAccount) {
        return '/v-agency/reset-password'
      }
      return '/v-admin/reset-password'
    }
  },
  watch: {
    isShowUserMenu (newVal, oldVal) {
      if (!this.lightTheme) {
        return
      }
      this.dropdownListClass = 'processing'
      setTimeout(() => {
        if (newVal) {
          this.dropdownListClass = 'processing opening show'
        } else {
          this.dropdownListClass = 'processing closing'
        }
      }, 0)
    }
  }
}
</script>

<style lang="scss" scoped>
.user-menu {
  position: relative;
  top: -9px;
  height: 30px;
  line-height: 30px;
  cursor: pointer;

  & > * {
    vertical-align: middle;
  }

  .dropdown-label {
    width: 140px;
    height: 28px;
    padding: 5px 28px 5px 10px;
    background: url($baseDomain + 'static/images_en/sprite_logo.png?t=1') no-repeat 0 -65px;
    line-height: 18px;
    font-weight: bold;
    box-sizing: border-box;

    .dropdown-icon {
      display: none;
    }
  }

  .dropdown-list {
    min-width: 100%;
    background: #666;
    position: absolute;
    top: 29px;
    z-index: 5;

    .dropdown-item {
      white-space: nowrap;
      word-break: keep-all;
      height: 30px;
      padding: 0 8px;
      cursor: pointer;
      position: relative;

      &:hover {
        background: #1a1a1a;
      }
    }

    .dropdown-arrow {
      position: absolute;
      border-width: 8px;
      border-color: transparent;
      border-style: solid;
      top: -6px;
      left: 74px;
      margin-right: 4px;
      border-top-width: 0;
      border-bottom-color: var(--user-menu-background-color, #fff);
    }
  }

  .notification-count {
    display: inline-block;
    height: 16px;
    line-height: 16px;
    margin-left: 5px;
    padding: 0px 5px;
    border-radius: 8px;
    background: #ff0000;
    color: #fff;
    text-align: center;
    font-size: 12px;
    position: absolute;
    top: 6px;
    right: 18px;
  }

  &.light-theme {
    top: -6px;

    .dropdown-label {
      width: 200px;
      height: inherit;
      line-height: inherit;
      padding: 0 10px;
      background: var(--user-menu-background-color, #f5f5f5);
      border-radius: 5px;
      color: var(--user-menu-font-color);

      .user-name {
        display: inline-block;
        width: 120px;
        word-break: keep-all;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: middle;
        color: var(--user-menu-font-color, #606266);
      }

      .dropdown-icon {
        display: inline-block;
        margin-left: 30px;
        vertical-align: middle;
        color: #777777;
      }

      .notification-count {
        margin-left: 0;
        transform: translateY(-50%);
        top: 50%;
        right: 25px;
      }
    }

    .dropdown-list {
      display: none;
      padding: 5px;
      line-height: 30px;
      border-radius: 5px;
      background: var(--user-menu-background-color, #fff);
      box-shadow: 2px 2px 8px 2px rgba(0, 0, 0, .15);
      opacity: 0;
      transition: opacity .3s ease-in-out;
      top: 52px;

      &.processing {
        display: block;
      }

      &.show {
        display: block;
        opacity: 1;
      }

      .dropdown-item {
        color: var(--user-menu-font-color);
        border-radius: 5px;

        &:hover {
          background: #f6f8fa;
        }
      }
    }
  }
}
</style>
